import {
  type JSX,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { MdClose } from 'react-icons/md';

import {
  Dialog,
  DialogContent,
  type DialogProps,
  DialogTitle,
  IconButton,
  Stack,
  type SxProps,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';

import { PopupTransition } from '@components';

interface IModal {
  sx?: SxProps<Theme>;
  isModalOpen: boolean;
  withCloseIcon?: boolean;
  title: string | ReactNode;
  disableBackDrop?: boolean;
  titleChildren?: ReactNode;
  handleCloseModal: () => void;
  dialogTitleSx?: SxProps<Theme>;
  slots?: NonNullable<DialogProps['slots']>;
  slotProps?: NonNullable<DialogProps['slotProps']>;
  maxWidth?: Exclude<DialogProps['maxWidth'], undefined>;
}

export const Modal = ({
  title,
  slots,
  sx = {},
  children,
  slotProps,
  isModalOpen,
  dialogTitleSx,
  maxWidth = 'sm',
  handleCloseModal,
  withCloseIcon = true,
  titleChildren = null,
  disableBackDrop = true,
}: PropsWithChildren<IModal>): JSX.Element => {
  const { t } = useTranslation();

  const closeHandler = useCallback(
    (_: unknown, reason: string) => {
      if (!(disableBackDrop && reason === 'backdropClick')) {
        handleCloseModal();
      }
    },
    [disableBackDrop, handleCloseModal]
  );

  return (
    <Dialog
      maxWidth={maxWidth}
      onClose={closeHandler}
      open={isModalOpen}
      scroll="paper"
      slotProps={slotProps}
      slots={{ transition: PopupTransition, ...slots }}
      sx={sx}
    >
      <DialogTitle sx={dialogTitleSx}>
        <Stack
          sx={{
            width: '100%',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {typeof title === 'string' ? (
            <Typography variant="h3">{t(title)}</Typography>
          ) : (
            title
          )}
          {withCloseIcon ? (
            <IconButton
              aria-label={t('common.button.close')}
              color="primary"
              onClick={handleCloseModal}
              size="large"
              sx={{ marginLeft: '1rem', marginRight: '-1rem' }}
            >
              <MdClose />
            </IconButton>
          ) : null}
        </Stack>
        {titleChildren}
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
    </Dialog>
  );
};
